import { Stack } from '@mui/joy';
import {
  IPortfolioDefinitionSubType,
  IPortfolioDefinitionType,
  type IRebalancedPortfolioFilterInput,
  useAssetsSuspenseQuery,
  useComputeMultifactorScoresLazyQuery,
  useMultifactorSuspenseQuery,
} from 'generated/graphql';
import DialogButton from 'components/technical/inputs/GButton/DialogButton';
import { type ReactElement, Suspense, useState } from 'react';
import {
  convertFiltersToGridState,
  convertGridStateToFilters,
  type LoadMultifactorFormInputFields,
  type LoadMultifactorFormOutputFields,
} from './AssetScreenerService';
import AssetScreenerTable from './AssetScreenerTable';
import { convertDateInUtcToUTCISODate } from 'components/date.utils';
import Loader from 'components/technical/Loader/Loader';
import LoadMultifactorDialog from './LoadMultifactorDialog';
import { Save } from '@mui/icons-material';

import { useLocation, useNavigate } from 'react-router-dom';
import { UserSettings } from 'components/management/UserSettings.types';
import type { RebalancedMultifactorPortfolioDefinitionActionsState } from 'components/copilot/lab/definition/PortfolioDefinitionActions';
import CreateUpdatePortfolioDialog from 'components/copilot/lab/definition/update/CreateUpdatePortfolioDialog';
import isNil from 'lodash/fp/isNil';

const AssetScreenerTab = ({
  portfolioDefinition,
}: {
  portfolioDefinition: RebalancedMultifactorPortfolioDefinitionActionsState | undefined;
}): ReactElement => {
  const defaultFilters = portfolioDefinition?.multifactor?.filters;
  const [filters, setFilters] = useState<IRebalancedPortfolioFilterInput[]>(defaultFilters ?? []);
  const getAllMultifactorsForUserQuery = useMultifactorSuspenseQuery();
  const assets = useAssetsSuspenseQuery();
  const navigate = useNavigate();
  const [fetchScores, { data: computedScores, loading: isComputedScoresLoading }] =
    useComputeMultifactorScoresLazyQuery();

  const handleSubmit = (data: LoadMultifactorFormInputFields): void => {
    const outData = data as unknown as LoadMultifactorFormOutputFields;
    fetchScores({
      variables: {
        input: {
          date: convertDateInUtcToUTCISODate(outData.date),
          screeners: outData.screeners,
        },
      },
    });
  };

  if (isComputedScoresLoading) {
    return <Loader />;
  }

  return (
    <Stack spacing={1.5} height="100%">
      <Stack direction="row" justifyContent="flex-end" gap={1}>
        {
          <DialogButton
            variant="soft"
            width="normal"
            renderDialog={({ onClose }): ReactElement => {
              return (
                <CreateUpdatePortfolioDialog
                  onClose={onClose}
                  onCompleted={() => {
                    onClose();
                    navigate('/app/copilot/lab/portfolio');
                  }}
                  def={{
                    ...portfolioDefinition,
                    id: portfolioDefinition?.id,
                    type: IPortfolioDefinitionType.Rebalanced,
                    subType: IPortfolioDefinitionSubType.MultifactorBased,
                    realDefAccounts: [],
                    realDefSubFunds: [],
                    rebalancedDetails: {
                      defaultAssetComposition: [],
                      transactionTag: '',
                      multifactorRule: {
                        ...(portfolioDefinition?.multifactor ?? {}),
                        filters,
                      },
                    },
                  }}
                  multifactors={getAllMultifactorsForUserQuery.data.multifactor.getAllMultifactorsForUser}
                />
              );
            }}
            startDecorator={<Save />}
          >
            {portfolioDefinition?.id ? 'Update' : 'Save'} portfolio
          </DialogButton>
        }
        <DialogButton
          renderDialog={({ onClose }): ReactElement => (
            <LoadMultifactorDialog
              onClose={onClose}
              handleSubmit={handleSubmit}
              multifactorQuery={getAllMultifactorsForUserQuery}
            />
          )}
          width="normal"
          disabled={!getAllMultifactorsForUserQuery.data.multifactor.getAllMultifactorsForUser.length}
        >
          Load multifactors
        </DialogButton>
      </Stack>

      <Suspense fallback={<Loader />}>
        <AssetScreenerTable
          onFiltersChanged={(model) => {
            setFilters(convertGridStateToFilters(model));
          }}
          scores={computedScores?.multifactor.computeMultifactorScores.scores!}
          presetKey={UserSettings.AssetScreenerPresets}
          assets={assets.data?.assets.feature ?? []}
          // don't pass default filters when there is no portfolio to not reset presets
          defaultFilters={isNil(defaultFilters) ? null : convertFiltersToGridState(defaultFilters)}
        />
      </Suspense>
    </Stack>
  );
};

const AssetScreenerTabContainer = (): ReactElement => {
  const location = useLocation();
  const routerState = location.state as RebalancedMultifactorPortfolioDefinitionActionsState | undefined;

  return <AssetScreenerTab portfolioDefinition={routerState} />;
};

export default AssetScreenerTabContainer;
